import { useEffect, useState } from 'react'
import { createClient } from '@sanity/client'
import imageUrlBuilder from '@sanity/image-url'

const projectId = process.env.REACT_APP_SANITY_PROJECT_ID
const dataset = process.env.REACT_APP_SANITY_DATASET || 'production'

// Without a project id the site simply keeps its hardcoded content.
// No token: only published documents are readable, so drafts never reach the site.
export const sanity = projectId
  ? createClient({ projectId, dataset, apiVersion: '2024-10-01', useCdn: true })
  : null

const builder = sanity ? imageUrlBuilder(sanity) : null

/** Image URL at `width`, or cropped to width × height around the hotspot when `height` is given. */
export function sanityImageUrl(image, width, height) {
  if (!builder || !image?.asset) return null
  const img = builder.image(image).width(width).auto('format')
  return (height ? img.height(height).fit('crop') : img.fit('max')).url()
}

const FETCH_TIMEOUT_MS = 10000
const cache = new Map()

/**
 * Fetches a GROQ query once per page load.
 * status: 'loading' | 'ready' | 'error' ('error' also covers unconfigured or timed out),
 * so callers can fall back to their hardcoded content.
 */
export function useSanityQuery(query, params) {
  const key = query + JSON.stringify(params || {})
  const initial = () =>
    cache.has(key)
      ? { data: cache.get(key), status: 'ready' }
      : { data: null, status: sanity ? 'loading' : 'error' }
  const [state, setState] = useState(initial)

  useEffect(() => {
    if (!sanity) return
    if (cache.has(key)) {
      setState({ data: cache.get(key), status: 'ready' })
      return
    }
    setState({ data: null, status: 'loading' })
    let cancelled = false
    const timeout = new Promise((_, reject) =>
      setTimeout(() => reject(new Error('Sanity request timed out')), FETCH_TIMEOUT_MS)
    )
    Promise.race([sanity.fetch(query, params || {}), timeout])
      .then((data) => {
        cache.set(key, data)
        if (!cancelled) setState({ data, status: 'ready' })
      })
      .catch((err) => {
        console.warn('Sanity fetch failed, using default content:', err)
        if (!cancelled) setState({ data: null, status: 'error' })
      })
    return () => {
      cancelled = true
    }
  }, [key])

  return state
}
